import React, { useState, useEffect, useCallback } from 'react';
import { Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend as ChartJsLegend,
  ArcElement,
} from 'chart.js';
import { X, Users, CheckCircle, Clock, XCircle, ChevronLeft } from 'lucide-react';
import API from '../../api/axios';
import toast from 'react-hot-toast';

Chart.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, ChartJsLegend, ArcElement);

// ─── Global CSS ───────────────────────────────────────────────────────────────
const globalCSS = `
  @import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;500;600;700;800;900&display=swap');

  :root {
    --ao-bg: #0f1117;
    --ao-card: #171923;
    --ao-border: rgba(255,255,255,0.07);
    --ao-text: #f0f2f5;
    --ao-muted: #8892a4;
    --ao-accent: #6366f1;
    --ao-gradient: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  }

  @keyframes ao-spin    { to { transform: rotate(360deg); } }
  @keyframes ao-fadeUp  { from { opacity:0; transform:translateY(14px); } to { opacity:1; transform:none; } }
  @keyframes ao-overlayIn { from { opacity:0; } to { opacity:1; } }
  @keyframes ao-slideUp { from { opacity:0; transform:translateY(40px); } to { opacity:1; transform:none; } }

  .ao-stat-card {
    cursor: pointer !important;
    transition: transform 0.18s, box-shadow 0.18s, border-color 0.18s !important;
  }
  .ao-stat-card:hover {
    transform: translateY(-4px) !important;
    box-shadow: 0 12px 36px rgba(0,0,0,0.4) !important;
    border-color: rgba(99,102,241,0.35) !important;
  }
  .ao-stat-card:active { transform: translateY(-1px) !important; }

  .ao-refresh-btn:hover {
    background: rgba(99,102,241,0.12) !important;
    border-color: #6366f1 !important;
  }

  .ao-anim   { animation: ao-fadeUp 0.42s ease both; }
  .ao-anim-1 { animation-delay: 0.05s; }
  .ao-anim-2 { animation-delay: 0.10s; }
  .ao-anim-3 { animation-delay: 0.15s; }
  .ao-anim-4 { animation-delay: 0.20s; }
  .ao-anim-5 { animation-delay: 0.25s; }

  .ao-modal-member:hover { background: rgba(255,255,255,0.04) !important; }

  /* ── Responsive ── */
  @media (max-width: 768px) {
    .ao-charts-grid  { grid-template-columns: 1fr !important; }
    .ao-stats-grid   { grid-template-columns: 1fr 1fr !important; }
    .ao-wrapper      { padding: 1rem !important; }
    .ao-header       { flex-wrap: wrap; gap: 0.75rem !important; }
    .ao-refresh-btn  { margin-right: 0 !important; }
    .ao-modal-inner  { margin: 0 !important; border-radius: 20px 20px 0 0 !important; max-height: 90vh !important; }
    .ao-modal-overlay { align-items: flex-end !important; }
  }
  @media (max-width: 480px) {
    .ao-stats-grid { grid-template-columns: 1fr !important; }
    .ao-header-title { font-size: 1.15rem !important; }
  }
`;

// ─── Inline Styles ────────────────────────────────────────────────────────────
const styles = {
  wrapper: {
    fontFamily: "'Cairo','Tajawal',sans-serif",
    direction: 'rtl',
    padding: '2rem',
    minHeight: '100vh',
    background: 'var(--ao-bg)',
    color: 'var(--ao-text)',
    boxSizing: 'border-box',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    marginBottom: '2.5rem',
    flexWrap: 'wrap',
  },
  headerIcon: {
    width: 48, height: 48, borderRadius: 14,
    background: 'var(--ao-gradient)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 22, flexShrink: 0,
    boxShadow: '0 4px 20px rgba(99,102,241,0.35)',
  },
  headerTitle: { margin: 0, fontSize: '1.5rem', fontWeight: 700, color: 'var(--ao-text)' },
  headerSub:   { margin: '2px 0 0', fontSize: '0.85rem', color: 'var(--ao-muted)' },
  refreshBtn: {
    marginRight: 'auto', marginLeft: 0,
    padding: '0.5rem 1.2rem', borderRadius: 10,
    border: '1px solid var(--ao-border)',
    background: 'transparent', color: 'var(--ao-accent)',
    fontSize: '0.85rem', cursor: 'pointer',
    display: 'flex', alignItems: 'center', gap: 6,
    transition: 'all 0.2s', fontFamily: 'inherit',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(175px, 1fr))',
    gap: '1.25rem', marginBottom: '2rem',
  },
  statCard: {
    background: 'var(--ao-card)', borderRadius: 18,
    padding: '1.4rem 1.6rem',
    border: '1px solid var(--ao-border)',
    position: 'relative', overflow: 'hidden',
  },
  statIcon:  { fontSize: 28, marginBottom: 8, display: 'block' },
  statValue: { fontSize: '2rem', fontWeight: 800, lineHeight: 1, margin: '4px 0' },
  statLabel: { fontSize: '0.82rem', color: 'var(--ao-muted)', fontWeight: 500 },
  statBadge: {
    position: 'absolute', top: 14, left: 14,
    fontSize: '0.7rem', padding: '2px 8px', borderRadius: 20, fontWeight: 600,
  },
  chartsGrid: {
    display: 'grid', gridTemplateColumns: '1fr 1fr',
    gap: '1.5rem', marginBottom: '2rem',
  },
  chartCard: {
    background: 'var(--ao-card)', borderRadius: 20,
    border: '1px solid var(--ao-border)', padding: '1.5rem', position: 'relative',
  },
  chartTitle: {
    margin: '0 0 1.2rem', fontSize: '1rem', fontWeight: 700,
    color: 'var(--ao-text)', display: 'flex', alignItems: 'center', gap: 8,
  },
  chartDot: { width: 8, height: 8, borderRadius: '50%', background: 'var(--ao-accent)', display: 'inline-block', flexShrink: 0 },
  legendRow: { display: 'flex', flexWrap: 'wrap', gap: '0.75rem', marginTop: '1rem' },
  legendItem: { display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8rem', color: 'var(--ao-muted)' },
  legendDot:  { width: 10, height: 10, borderRadius: 3, flexShrink: 0 },
  progressCard: {
    background: 'var(--ao-card)', borderRadius: 20,
    border: '1px solid var(--ao-border)', padding: '1.5rem', marginBottom: '1.5rem',
  },
  progressHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' },
  progressLabel:  { fontSize: '0.9rem', fontWeight: 600, color: 'var(--ao-text)' },
  progressValue:  { fontSize: '0.85rem', color: 'var(--ao-muted)' },
  progressBar:  { height: 10, borderRadius: 99, background: 'var(--ao-border)', overflow: 'hidden' },
  progressFill: { height: '100%', borderRadius: 99, background: 'var(--ao-gradient)', transition: 'width 1s ease' },
  loadingWrapper: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', minHeight: '60vh', gap: '1rem',
    color: 'var(--ao-muted)', fontFamily: "'Cairo',sans-serif", direction: 'rtl',
  },
  spinner: {
    width: 44, height: 44,
    border: '3px solid rgba(255,255,255,0.07)',
    borderTop: '3px solid #6366f1',
    borderRadius: '50%', animation: 'ao-spin 0.8s linear infinite',
  },
};

// ─── Tooltip defaults ─────────────────────────────────────────────────────────
const tooltipDefaults = {
  backgroundColor: '#1e2130',
  borderColor: 'rgba(255,255,255,0.08)', borderWidth: 1,
  titleFont: { family: 'Cairo', size: 13 },
  bodyFont:  { family: 'Cairo', size: 12 },
  titleColor: '#f0f2f5', bodyColor: '#8892a4',
  padding: 12, cornerRadius: 10,
};

// ─── ChartLegend ──────────────────────────────────────────────────────────────
const ChartLegend = ({ items }) => (
  <div style={styles.legendRow}>
    {items.map(item => (
      <div key={item.label} style={styles.legendItem}>
        <div style={{ ...styles.legendDot, background: item.color }} />
        {item.label}
      </div>
    ))}
  </div>
);

// ─── StatCard ─────────────────────────────────────────────────────────────────
const StatCard = ({ icon, value, label, color, badge, badgeColor, animClass, onClick }) => (
  <div
    style={styles.statCard}
    className={'ao-stat-card ao-anim ' + (animClass || '')}
    onClick={onClick}
    title="اضغط للتفاصيل"
  >
    {badge && (
      <span style={{ ...styles.statBadge, background: badgeColor + '22', color: badgeColor }}>
        {badge}
      </span>
    )}
    <div style={{
      position: 'absolute', bottom: -20, left: -20,
      width: 90, height: 90, borderRadius: '50%',
      background: color + '18', filter: 'blur(20px)', pointerEvents: 'none',
    }} />
    <span style={styles.statIcon}>{icon}</span>
    <div style={{ ...styles.statValue, color }}>{value}</div>
    <div style={styles.statLabel}>{label}</div>
    {/* click hint */}
    <div style={{ fontSize: '0.68rem', color: color + 'aa', marginTop: 6, display: 'flex', alignItems: 'center', gap: 3 }}>
      <ChevronLeft size={11} /> عرض التفاصيل
    </div>
  </div>
);

// ─── Detail Modal ─────────────────────────────────────────────────────────────
const DetailModal = ({ type, group, onClose }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const config = {
    members: {
      title: 'أعضاء المجموعة',
      icon: <Users size={20} />,
      color: '#6366f1',
      emptyMsg: 'لا يوجد أعضاء حتى الآن',
    },
    confirmed: {
      title: 'مدفوعات مؤكدة',
      icon: <CheckCircle size={20} />,
      color: '#10b981',
      emptyMsg: 'لا يوجد مدفوعات مؤكدة',
    },
    pending: {
      title: 'بانتظار التأكيد',
      icon: <Clock size={20} />,
      color: '#f59e0b',
      emptyMsg: 'لا يوجد مدفوعات معلقة',
    },
    notpaid: {
      title: 'لم يدفعوا بعد',
      icon: <XCircle size={20} />,
      color: '#ef4444',
      emptyMsg: 'الجميع دفع! 🎉',
    },
  };

  const cfg = config[type];

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        if (type === 'members') {
          const { data: res } = await API.get(`/groups/${group._id}/members`);
          // combine admins + members
          const admins  = (res.admins  || []).map(m => ({ ...m, role: 'admin' }));
          const members = (res.members || []).map(m => ({ ...m, role: 'member' }));
          setData([...admins, ...members]);
        } else {
          // payment statuses — fetch all payments and filter
          const { data: res } = await API.get(`/payments/group/${group._id}/all`);
          const all = res.memberPayments || [];
          if (type === 'confirmed') {
            setData(all.filter(p => p.allConfirmed && p.payments?.length > 0).map(p => p.user));
          } else if (type === 'pending') {
            setData(all.filter(p => p.hasPending).map(p => p.user));
          } else if (type === 'notpaid') {
            setData(all.filter(p => !p.allConfirmed && !p.hasPending).map(p => p.user));
          }
        }
      } catch {
        toast.error('فشل تحميل البيانات');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [type, group._id]);

  return (
    <div
      className="ao-modal-overlay"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.65)',
        backdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 9999, padding: '20px',
        animation: 'ao-overlayIn 0.2s ease',
      }}
    >
      <div
        className="ao-modal-inner"
        onClick={e => e.stopPropagation()}
        style={{
          background: '#171923',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 22,
          width: '100%', maxWidth: 480,
          maxHeight: '80vh',
          display: 'flex', flexDirection: 'column',
          animation: 'ao-slideUp 0.28s cubic-bezier(0.34,1.4,0.64,1)',
          boxShadow: '0 32px 80px rgba(0,0,0,0.6)',
          overflow: 'hidden',
          fontFamily: "'Cairo','Tajawal',sans-serif",
          direction: 'rtl',
        }}
      >
        {/* Modal header */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '18px 20px',
          borderBottom: '1px solid rgba(255,255,255,0.07)',
          flexShrink: 0,
        }}>
          <div style={{
            width: 40, height: 40, borderRadius: 12,
            background: cfg.color + '1a',
            border: '1px solid ' + cfg.color + '40',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: cfg.color,
          }}>
            {cfg.icon}
          </div>
          <div style={{ flex: 1 }}>
            <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 800, color: '#f0f2f5' }}>
              {cfg.title}
            </h3>
            <p style={{ margin: '2px 0 0', fontSize: '0.75rem', color: '#8892a4' }}>
              {loading ? '...' : data.length + ' شخص'}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              width: 32, height: 32, borderRadius: 9,
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.1)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', color: '#8892a4',
            }}
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div style={{ overflowY: 'auto', flex: 1, padding: '12px 0' }}>
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
              <div style={styles.spinner} />
            </div>
          ) : data.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#8892a4' }}>
              <div style={{ fontSize: 36, marginBottom: 10 }}>
                {type === 'notpaid' ? '🎉' : '📭'}
              </div>
              {cfg.emptyMsg}
            </div>
          ) : (
            data.map((member, i) => member && (
              <MemberItem key={member._id || i} member={member} color={cfg.color} type={type} />
            ))
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Member Item inside modal ─────────────────────────────────────────────────
const MemberItem = ({ member, color, type }) => {
  const roleLabel = member.role === 'admin' ? 'مشرف' : null;
  const initials = (member.name || '?').charAt(0).toUpperCase();

  return (
    <div
      className="ao-modal-member"
      style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '10px 20px', transition: 'background 0.15s',
      }}
    >
      {/* Avatar */}
      {member.avatar ? (
        <img
          src={member.avatar} alt={member.name}
          style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
          onError={e => { e.target.style.display = 'none'; }}
        />
      ) : (
        <div style={{
          width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
          background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '1rem', fontWeight: 700, color: '#fff',
        }}>
          {initials}
        </div>
      )}

      {/* Info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: '0.88rem', fontWeight: 700, color: '#f0f2f5' }}>
            {member.name || 'مجهول'}
          </span>
          {roleLabel && (
            <span style={{
              fontSize: '0.66rem', padding: '1px 7px', borderRadius: 20,
              background: 'rgba(251,191,36,0.15)', color: '#fbbf24', fontWeight: 700,
            }}>
              {roleLabel}
            </span>
          )}
        </div>
        {member.email && (
          <div style={{ fontSize: '0.75rem', color: '#8892a4', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {member.email}
          </div>
        )}
      </div>

      {/* Status dot */}
      <div style={{
        width: 8, height: 8, borderRadius: '50%',
        background: color, flexShrink: 0,
        boxShadow: '0 0 6px ' + color + '80',
      }} />
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const AdminOverview = ({ group }) => {
  const [stats, setStats]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [modal, setModal]     = useState(null); // 'members' | 'confirmed' | 'pending' | 'notpaid'

  const loadStats = useCallback(async () => {
    setLoading(true);
    try {
      const res = await API.get(`/groups/${group._id}/stats`);
      setStats(res.data);
    } catch {
      toast.error('فشل تحميل الإحصائيات');
    } finally {
      setLoading(false);
    }
  }, [group._id]);

  useEffect(() => { if (group?._id) loadStats(); }, [group, loadStats]);

  if (loading)
    return (
      <>
        <style>{globalCSS}</style>
        <div style={styles.loadingWrapper}>
          <div style={styles.spinner} />
          <span>جاري تحميل الإحصائيات...</span>
        </div>
      </>
    );

  if (!stats) return null;

  const occupancyPct = Math.round((stats.totalMembers / stats.maxMembers) * 100);
  const payTotal =
    (stats.paymentStats.confirmed || 0) +
    (stats.paymentStats.pending   || 0) +
    (stats.paymentStats.notpaid   || 0);

  const memberBarData = {
    labels: ['الأعضاء الحاليون', 'المقاعد الشاغرة'],
    datasets: [{
      data: [stats.totalMembers, stats.maxMembers - stats.totalMembers],
      backgroundColor: ['#6366f1', 'rgba(99,102,241,0.15)'],
      hoverBackgroundColor: ['#818cf8', 'rgba(99,102,241,0.25)'],
      borderRadius: 10, borderSkipped: false,
    }],
  };

  const paymentDoughnutData = {
    labels: ['مؤكدة', 'قيد الانتظار', 'لم تدفع'],
    datasets: [{
      data: [stats.paymentStats.confirmed, stats.paymentStats.pending, stats.paymentStats.notpaid],
      backgroundColor: ['#10b981', '#f59e0b', '#ef4444'],
      hoverBackgroundColor: ['#34d399', '#fbbf24', '#f87171'],
      borderWidth: 0, hoverOffset: 6,
    }],
  };

  const barOptions = {
    responsive: true, maintainAspectRatio: true,
    plugins: { legend: { display: false }, tooltip: tooltipDefaults },
    scales: {
      x: { grid: { display: false }, ticks: { color: '#8892a4', font: { family: 'Cairo', size: 12 } }, border: { color: 'transparent' } },
      y: { grid: { color: 'rgba(255,255,255,0.04)' }, ticks: { color: '#8892a4', font: { family: 'Cairo', size: 11 } }, border: { color: 'transparent' } },
    },
  };

  const doughnutOptions = {
    responsive: true, maintainAspectRatio: true, cutout: '72%',
    plugins: { legend: { display: false }, tooltip: tooltipDefaults },
  };

  return (
    <>
      <style>{globalCSS}</style>
      <div style={styles.wrapper} className="ao-wrapper">

        {/* Header */}
        <div style={styles.header} className="ao-header">
          <div style={styles.headerIcon}>📊</div>
          <div>
            <h2 style={styles.headerTitle} className="ao-header-title">لوحة إحصائيات المجموعة</h2>
            <p style={styles.headerSub}>{group?.name || 'نظرة عامة شاملة'}</p>
          </div>
          <button style={styles.refreshBtn} className="ao-refresh-btn" onClick={loadStats}>
            ↻ تحديث
          </button>
        </div>

        {/* ── Stat Cards (clickable) ── */}
        <div style={styles.statsGrid} className="ao-stats-grid">
          <StatCard
            icon="👥" value={stats.totalMembers} label="عدد الأعضاء الحاليين"
            color="#6366f1" badge={occupancyPct + '%'} badgeColor="#6366f1"
            animClass="ao-anim-1" onClick={() => setModal('members')}
          />
          <StatCard
            icon="🏷️" value={stats.maxMembers} label="الحد الأقصى للأعضاء"
            color="#8b5cf6" animClass="ao-anim-2"
            // no popup for max members — just visual
            onClick={() => toast('الحد الأقصى للمجموعة: ' + stats.maxMembers + ' عضو', { icon: '🏷️' })}
          />
          <StatCard
            icon="✅" value={stats.paymentStats.confirmed} label="مدفوعات مؤكدة"
            color="#10b981" badge="مؤكد" badgeColor="#10b981"
            animClass="ao-anim-3" onClick={() => setModal('confirmed')}
          />
          <StatCard
            icon="⏳" value={stats.paymentStats.pending} label="بانتظار التأكيد"
            color="#f59e0b" badge="معلق" badgeColor="#f59e0b"
            animClass="ao-anim-4" onClick={() => setModal('pending')}
          />
          <StatCard
            icon="❌" value={stats.paymentStats.notpaid} label="لم يدفعوا بعد"
            color="#ef4444" animClass="ao-anim-5" onClick={() => setModal('notpaid')}
          />
        </div>

        {/* Occupancy Progress */}
        <div style={styles.progressCard} className="ao-anim ao-anim-1">
          <div style={styles.progressHeader}>
            <span style={styles.progressLabel}>نسبة امتلاء المجموعة</span>
            <span style={styles.progressValue}>{stats.totalMembers} / {stats.maxMembers} عضو</span>
          </div>
          <div style={styles.progressBar}>
            <div style={{ ...styles.progressFill, width: occupancyPct + '%' }} />
          </div>
          <div style={{ textAlign: 'left', fontSize: '0.78rem', color: 'var(--ao-muted)', marginTop: 6 }}>
            {occupancyPct}% ممتلئة
          </div>
        </div>

        {/* Charts */}
        <div style={styles.chartsGrid} className="ao-charts-grid">

          <div style={styles.chartCard} className="ao-anim ao-anim-2">
            <h4 style={styles.chartTitle}>
              <span style={styles.chartDot} /> توزيع الأعضاء
            </h4>
            <Bar data={memberBarData} options={barOptions} />
            <ChartLegend items={[
              { label: 'الأعضاء الحاليون', color: '#6366f1' },
              { label: 'المقاعد الشاغرة', color: 'rgba(99,102,241,0.3)' },
            ]} />
          </div>

          <div style={styles.chartCard} className="ao-anim ao-anim-3">
            <h4 style={styles.chartTitle}>
              <span style={{ ...styles.chartDot, background: '#10b981' }} /> حالة المدفوعات
            </h4>
            <div style={{ position: 'relative', maxWidth: 240, margin: '0 auto' }}>
              <Doughnut data={paymentDoughnutData} options={doughnutOptions} />
              <div style={{
                position: 'absolute', top: '50%', left: '50%',
                transform: 'translate(-50%, -50%)',
                textAlign: 'center', pointerEvents: 'none',
              }}>
                <div style={{ fontSize: '1.6rem', fontWeight: 800, color: 'var(--ao-text)', lineHeight: 1 }}>
                  {payTotal}
                </div>
                <div style={{ fontSize: '0.72rem', color: 'var(--ao-muted)', marginTop: 2 }}>إجمالي</div>
              </div>
            </div>
            <ChartLegend items={[
              { label: 'مؤكدة (' + stats.paymentStats.confirmed + ')', color: '#10b981' },
              { label: 'قيد الانتظار (' + stats.paymentStats.pending + ')', color: '#f59e0b' },
              { label: 'لم تدفع (' + stats.paymentStats.notpaid + ')', color: '#ef4444' },
            ]} />
          </div>
        </div>

        {/* Payment breakdown bars */}
        <div style={styles.chartCard} className="ao-anim ao-anim-4">
          <h4 style={styles.chartTitle}>
            <span style={{ ...styles.chartDot, background: '#3b82f6' }} /> تفاصيل المدفوعات
          </h4>
          {[
            { label: 'مدفوعات مؤكدة', value: stats.paymentStats.confirmed, color: '#10b981', type: 'confirmed' },
            { label: 'قيد الانتظار',  value: stats.paymentStats.pending,   color: '#f59e0b', type: 'pending'   },
            { label: 'لم يدفعوا',     value: stats.paymentStats.notpaid,   color: '#ef4444', type: 'notpaid'   },
          ].map(row => (
            <div
              key={row.label}
              style={{ marginBottom: '1rem', cursor: 'pointer' }}
              onClick={() => setModal(row.type)}
              title="اضغط لعرض الأشخاص"
            >
              <div style={styles.progressHeader}>
                <span style={{ ...styles.progressLabel, fontSize: '0.85rem' }}>{row.label}</span>
                <span style={{ ...styles.progressValue, color: row.color, fontWeight: 700 }}>
                  {row.value} / {payTotal}
                </span>
              </div>
              <div style={styles.progressBar}>
                <div style={{
                  ...styles.progressFill,
                  width: payTotal ? ((row.value / payTotal) * 100) + '%' : '0%',
                  background: row.color,
                }} />
              </div>
            </div>
          ))}
        </div>

      </div>

      {/* Detail Modal */}
      {modal && (
        <DetailModal
          type={modal}
          group={group}
          onClose={() => setModal(null)}
        />
      )}
    </>
  );
};

export default AdminOverview;